// @ts-nocheck
import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, Search, BarChart2, GitCompare, Loader2 } from 'lucide-react';
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { formatTicker, isValidCompanyTicker } from '../utils/tickerUtils';
import { processCompanyData } from '../utils/rankingParser';

interface CompareData {
  ticker: string;
  name: string;
  price: number;
  roe: number;
  roic: number;
  netMargin: number;
  ebitdaMargin: number;
  peRatio: number;
  dividendYield: number;
  revenueGrowth5y: number;
}

function CompareSearchInput({ label, value, onChange, placeholder, onSubmit }: any) {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchSuggestions = async (query: string) => {
    if (!query.trim()) {
      setSuggestions([]);
      return;
    }
    
    setIsSearching(true);
    try {
      const response = await fetch(`/api/search?q=${encodeURIComponent(query)}`);
      if (response.ok) {
        const data = await response.json();
        if (data.quotes) {
          const formattedSuggestions = data.quotes
            .filter((q: any) => {
              if (q.quoteType === 'INDEX' || q.quoteType === 'OPTION') return false;
              return isValidCompanyTicker(q.symbol);
            })
            .map((q: any) => ({
              ticker: formatTicker(q.symbol),
              name: q.shortname || q.longname || q.symbol
            }));
          setSuggestions(formattedSuggestions);
        }
      }
    } catch (error) {
      console.error("Error fetching suggestions:", error);
    } finally {
      setIsSearching(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    onChange(val);
    setShowSuggestions(true);

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      fetchSuggestions(val);
    }, 300);
  };

  const handleSuggestionClick = (suggestionTicker: string) => {
    onChange(suggestionTicker);
    setShowSuggestions(false);
  };

  return (
    <div className="flex-1 relative" ref={wrapperRef}>
      <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">{label}</label>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input 
          type="text" 
          value={value}
          onChange={handleInputChange}
          onFocus={() => setShowSuggestions(true)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              setShowSuggestions(false);
              onSubmit();
            }
          }}
          placeholder={placeholder}
          className="w-full pl-10 pr-4 py-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none font-medium uppercase text-slate-800 dark:text-slate-200 transition-all"
        />
      </div>

      <AnimatePresence>
        {showSuggestions && value.trim() && (suggestions.length > 0 || isSearching) && (
          <motion.div
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 5 }}
            className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl overflow-hidden z-20"
          >
            {isSearching && suggestions.length === 0 ? (
              <div className="flex items-center justify-center py-4 text-slate-500 text-sm font-medium">
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Buscando...
              </div>
            ) : (
              <div className="max-h-60 overflow-y-auto hide-scrollbar">
                {suggestions.slice(0, 5).map((suggestion) => (
                  <button
                    key={suggestion.ticker}
                    onClick={() => handleSuggestionClick(suggestion.ticker)}
                    className="w-full text-left px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors flex items-center gap-3 border-b border-slate-100 dark:border-slate-700/50 last:border-0"
                  >
                    <div className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-slate-700 flex items-center justify-center text-xs font-bold text-slate-700 dark:text-slate-300">
                      {suggestion.ticker.slice(0, 2)}
                    </div>
                    <div className="min-w-0">
                      <p className="font-bold text-sm text-slate-900 dark:text-white truncate">
                        {suggestion.ticker}
                      </p>
                      <p className="text-xs text-slate-500 truncate">
                        {suggestion.name}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function QuickCompareOverlay() {
  const [isOpen, setIsOpen] = useState(false);
  const [ticker1, setTicker1] = useState('');
  const [ticker2, setTicker2] = useState('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<{ t1: CompareData | null, t2: CompareData | null }>({ t1: null, t2: null });

  useEffect(() => {
    const handleOpen = () => setIsOpen(true);
    document.addEventListener('open-quick-compare', handleOpen);
    return () => document.removeEventListener('open-quick-compare', handleOpen);
  }, []);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchData = async () => {
    if (!ticker1 || !ticker2) return;
    setLoading(true);
    try {
      const formattedT1 = formatTicker(ticker1.trim().toUpperCase());
      const formattedT2 = formatTicker(ticker2.trim().toUpperCase());
      
      const res = await fetch(`/api/compare?tickers=${encodeURIComponent(formattedT1)},${encodeURIComponent(formattedT2)}`);
      
      if (!res.ok) throw new Error("Erro na API.");
      const comparisonResults = await res.json();

      const parseMetrics = (dbResult: any) => {
        if (!dbResult) return null;
        const processed = processCompanyData(dbResult);
        if (!processed) return null;
        
        return {
          ticker: processed.ticker,
          name: processed.companyName,
          price: processed.currentPrice || 0,
          roe: processed.roe || 0,
          roic: processed.roic || 0,
          netMargin: processed.netMargin || 0,
          ebitdaMargin: processed.ebitdaMargin || 0,
          peRatio: processed.peRatio || 0,
          dividendYield: processed.dividendYield || 0,
          revenueGrowth5y: processed.revenueGrowth5y || 0,
        };
      };

      const result1 = comparisonResults.find((r: any) => formatTicker(r.ticker).toUpperCase() === formattedT1);
      const result2 = comparisonResults.find((r: any) => formatTicker(r.ticker).toUpperCase() === formattedT2);

      setData({
        t1: parseMetrics(result1),
        t2: parseMetrics(result2)
      });
    } catch (e) {
      console.error(e);
      setData({ t1: null, t2: null });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setTicker1(''); setTicker2(''); setData({ t1: null, t2: null });
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const normalize = (val: number, v1: number, v2: number, invert: boolean = false) => {
    if (v1 === 0 && v2 === 0) return 0;
    
    if (!invert) {
      // Higher is better
      const max = Math.max(v1, v2);
      if (max <= 0) return 0; // both zero or negative, no edge
      if (val < 0) return 0; // negative value gets 0 area
      return Math.min(100, Math.max(0, (val / max) * 100));
    } else {
      // Lower is better (P/L)
      if (val <= 0) return 0; // Negative P/L is bad, 0
      
      const v1Pos = v1 > 0 ? v1 : Infinity;
      const v2Pos = v2 > 0 ? v2 : Infinity;
      const minPos = Math.min(v1Pos, v2Pos);
      
      if (minPos === Infinity) return 0;
      
      // If this value is the minimum positive, it gets 100
      if (val === minPos) return 100;
      
      return Math.min(100, Math.max(0, (minPos / val) * 100));
    }
  };

  const chartData = [];
  if (data.t1 && data.t2) {
    const keys = [
      { key: 'roe', label: 'ROE (%)' },
      { key: 'roic', label: 'ROIC (%)' },
      { key: 'netMargin', label: isMobile ? 'Mg. Líq(%)' : 'Mg Líquida (%)' },
      { key: 'ebitdaMargin', label: isMobile ? 'Mg. EBT(%)' : 'Mg EBITDA (%)' },
      { key: 'dividendYield', label: isMobile ? 'DY (%)' : 'Div Yield (%)' },
      { key: 'revenueGrowth5y', label: isMobile ? 'Cresc 5A(%)' : 'Crescimento 5A (%)' },
      { key: 'peRatio', label: 'P/L', invert: true },
    ];

    keys.forEach(({ key, label, invert }) => {
      const v1 = data.t1![key as keyof CompareData] as number;
      const v2 = data.t2![key as keyof CompareData] as number;
      
      chartData.push({
        subject: label,
        [data.t1!.ticker]: normalize(v1, v1, v2, invert),
        [data.t2!.ticker]: normalize(v2, v1, v2, invert),
        raw1: v1,
        raw2: v2
      });
    });
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
           initial={{ opacity: 0 }}
           animate={{ opacity: 1 }}
           exit={{ opacity: 0 }}
           className="fixed inset-0 z-[9999] bg-slate-900/40 dark:bg-black/60 backdrop-blur-md flex items-center justify-center p-4 sm:p-6"
           onClick={(e) => { if (e.target === e.currentTarget) setIsOpen(false) }}
        >
          <motion.div 
             initial={{ scale: 0.95, opacity: 0, y: 10 }}
             animate={{ scale: 1, opacity: 1, y: 0 }}
             exit={{ scale: 0.95, opacity: 0, y: 10 }}
             className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col"
          >
             <div className="flex items-center justify-between p-4 sm:p-6 border-b border-slate-100 dark:border-slate-800 shrink-0">
               <div className="flex items-center gap-3">
                 <div className="w-10 h-10 rounded-xl bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex items-center justify-center">
                   <GitCompare className="w-5 h-5" />
                 </div>
                 <div>
                   <h2 className="text-xl font-bold text-slate-800 dark:text-white leading-tight">Comparação Rápida</h2>
                   <p className="text-sm text-slate-500 dark:text-slate-400">Compare métricas de valuation</p>
                 </div>
               </div>
               <button onClick={() => setIsOpen(false)} className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors">
                  <X className="w-6 h-6" />
               </button>
             </div>

             <div className="p-4 sm:p-6 flex-1 overflow-y-auto">
                <div className="flex flex-col sm:flex-row gap-4 mb-8">
                  <CompareSearchInput
                    label="Ticker 1"
                    value={ticker1}
                    onChange={setTicker1}
                    placeholder="Ex: AAPL"
                    onSubmit={fetchData}
                  />
                  <CompareSearchInput
                    label="Ticker 2"
                    value={ticker2}
                    onChange={setTicker2}
                    placeholder="Ex: MSFT"
                    onSubmit={fetchData}
                  />
                  <div className="flex items-end">
                     <button 
                       onClick={fetchData}
                       disabled={loading || !ticker1 || !ticker2}
                       className="w-full sm:w-auto px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold rounded-xl transition-all h-[50px] shadow-lg shadow-blue-500/20 flex items-center justify-center gap-2"
                     >
                        {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                        Comparar
                     </button>
                  </div>
                </div>

                {data.t1 && data.t2 && (
                  <div className="bg-slate-50 dark:bg-slate-800/20 rounded-2xl p-6 border border-slate-100 dark:border-slate-800">
                    <div className="h-[350px] sm:h-[400px] w-full">
                      <ResponsiveContainer width="100%" height="100%">
                        <RadarChart cx="50%" cy="50%" outerRadius={isMobile ? "45%" : "65%"} data={chartData}>
                          <PolarGrid stroke="#cbd5e1" strokeDasharray="3 3"/>
                          <PolarAngleAxis dataKey="subject" tick={{ fill: '#64748b', fontSize: isMobile ? 11 : 12, fontWeight: 600 }} />
                          <Tooltip 
                            content={({ active, payload }) => {
                              if (active && payload && payload.length) {
                                return (
                                  <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-3 shadow-xl">
                                    <p className="font-bold text-slate-800 dark:text-white mb-2">{payload[0].payload.subject}</p>
                                    {payload.map((entry: any, index: number) => (
                                      <div key={index} className="flex items-center justify-between gap-4 mb-1">
                                        <span style={{ color: entry.color }} className="font-semibold text-sm">
                                          {entry.name}:
                                        </span>
                                        <span className="font-mono text-sm dark:text-slate-300">
                                          {entry.payload[`raw${index+1}`] !== undefined 
                                            ? Number(entry.payload[`raw${index+1}`]).toLocaleString('pt-BR', { maximumFractionDigits: 2 })
                                            : '?'}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                );
                              }
                              return null;
                            }}
                          />
                          <Radar name={data.t1.ticker} dataKey={data.t1.ticker} stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.4} />
                          <Radar name={data.t2.ticker} dataKey={data.t2.ticker} stroke="#10b981" fill="#10b981" fillOpacity={0.4} />
                          <Legend wrapperStyle={{ paddingTop: '20px' }} />
                        </RadarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}
                
                {data.t1 && !data.t2 && !loading && (
                   <p className="text-center text-slate-500 py-10">Não foi possível encontrar o segundo ativo ({ticker2.toUpperCase()}).</p>
                )}
                {!data.t1 && data.t2 && !loading && (
                   <p className="text-center text-slate-500 py-10">Não foi possível encontrar o primeiro ativo ({ticker1.toUpperCase()}).</p>
                )}
                {!data.t1 && !data.t2 && !loading && (ticker1 || ticker2) && (
                   <p className="text-center text-slate-500 py-10">Preencha ambos tickers e compare.</p>
                )}
             </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
